/* eslint-disable import/no-extraneous-dependencies */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { get } from 'lodash';
import { RootState } from 'redux/store';
import request from 'util/request';

type Status = 'idle' | 'loading' | 'success' | 'error';

export interface PayloadActionGetListProxy {
  region: string;
}

export interface PayloadActionListProxyDetail {
  id: number;
  slug: string;
  name: string;
  description: string;
  price: number;
  cpu: string;
  ram: string;
  ssd: string;
  bandwidth: string;
  ethernet_port: string;
  ipv4: string;
  status: string;
  region: string;
  featured: boolean;
}

export interface ISupportOption {
  username: {
    type: string;
    multichoice: string;
    option: {
      random: string;
    };
  };
  password: {
    type: string;
    multichoice: string;
    option: {
      random: string;
    };
  };
  random_password: {
    type: string;
    multichoice: string;
    option: {
      password: string;
    };
  };
  port: {
    type: string;
    multichoice: string;
    option: {
      port: string;
    };
  };
  nation: {
    type: string;
    multichoice: string;
    option: Record<string, string>;
  };
  type: {
    type: string;
    multichoice: string;
    option: Record<string, number | boolean | string>;
  };
  isp: {
    type: string;
    multichoice: string;
    option: Array<string>;
  };
  state: {
    type: string;
    multichoice: string;
    option: Array<string>;
  };
  range_ip: {
    type: string;
    multichoice: string;
    option: Array<string>;
  };
  duration: {
    type: string;
    multichoice: string;
    option: Record<string, number | boolean | string>;
  };
}

interface IGetListProxySupport {
  nation?: string;
  state?: string;
  isp?: string;
}

interface getListRegion {
  statusLoadListRegion: Status;
  statusLoadListSupportOption: Status;
  statusLoadingListProxy: Status;
  listSupportOption: ISupportOption;
  paramListProxy: PayloadActionGetListProxy;
  listProxyDetail: PayloadActionListProxyDetail[];
}

const initialState: getListRegion = {
  listSupportOption: {
    username: {
      type: 'checkbox',
      multichoice: 'false',
      option: {
        random: 'username ngẫu nhiên',
      },
    },
    password: {
      type: 'checkbox',
      multichoice: 'false',
      option: {
        random: 'password ngẫu nhiên',
      },
    },
    random_password: {
      type: 'checkbox',
      multichoice: 'false',
      option: {
        password: 'JCEjhi7146',
      },
    },
    port: {
      type: 'checkbox',
      multichoice: 'false',
      option: {
        port: 'port ngẫu nhiên',
      },
    },
    nation: {
      type: 'dropdown',
      multichoice: 'false',
      option: {},
    },
    type: {
      type: 'dropdown',
      multichoice: 'false',
      option: {},
    },
    isp: {
      type: 'dropdown',
      multichoice: 'false',
      option: [],
    },
    state: {
      type: 'dropdown',
      multichoice: 'false',
      option: [],
    },
    range_ip: {
      type: 'dropdown',
      multichoice: 'false',
      option: [],
    },
    duration: {
      type: 'dropdown',
      multichoice: 'false',
      option: {},
    },
  },
  paramListProxy: {
    region: '',
  },
  listProxyDetail: [],
  statusLoadListRegion: 'idle',
  statusLoadListSupportOption: 'idle',
  statusLoadingListProxy: 'idle',
};

export const actionGetListProxy = createAsyncThunk(
  'buyProxy/actionGetListProxy',
  async (params: PayloadActionGetListProxy, { rejectWithValue }) => {
    try {
      const response = await request({
        url: 'api/plan/proxy',
        method: 'GET',
        params,
      });

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const actionGetListProxySupportOption = createAsyncThunk(
  'buyProxy/actionGetListProxySupportOption',
  async (params: IGetListProxySupport, { rejectWithValue }) => {
    try {
      const response = await request({
        url: 'api/server/proxy/support',
        method: 'GET',
        params,
      });

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const slice = createSlice({
  name: 'buyProxy',
  initialState,
  reducers: {
    actionResetProxyDetailStore() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    // get list support option
    builder.addCase(actionGetListProxySupportOption.pending, (state) => {
      state.statusLoadListSupportOption = 'loading';
    });
    builder.addCase(actionGetListProxySupportOption.fulfilled, (state, action) => {
      state.statusLoadListSupportOption = 'success';
      state.listSupportOption = get(action, 'payload.data');
    });
    builder.addCase(actionGetListProxySupportOption.rejected, (state) => {
      state.statusLoadListSupportOption = 'error';
    });

    // get list Proxy detail
    builder.addCase(actionGetListProxy.pending, (state) => {
      state.statusLoadingListProxy = 'loading';
    });
    builder.addCase(actionGetListProxy.fulfilled, (state, action) => {
      state.statusLoadingListProxy = 'success';
      state.listProxyDetail = get(action, 'payload.data').map((item: PayloadActionListProxyDetail) => ({ ...item }));
    });
    builder.addCase(actionGetListProxy.rejected, (state) => {
      state.statusLoadingListProxy = 'error';
    });
  },
});

export const { actionResetProxyDetailStore } = slice.actions;

export const selectLoadingListRegion = (state: RootState) => state.buyProxy.statusLoadListRegion;
export const selectListSupportOption = (state: RootState) => state.buyProxy.listSupportOption;
export const selectLoadingListSupportOption = (state: RootState) => state.buyProxy.statusLoadListSupportOption;
export const selectListProxyDetail = (state: RootState) => state.buyProxy.listProxyDetail;
export const selectLoadingListProxy = (state: RootState) => state.buyProxy.statusLoadingListProxy;

export default slice.reducer;
